import logger from './logger';
import { analytics } from './analytics';

interface ErrorReportOptions {
    componentStack?: string;
    tags?: Record<string, string>;
    extra?: Record<string, any>;
}

/**
 * Reports an error to the global error tracking service.
 * Currently logs to console via logger, but can be extended for Sentry/LogRocket etc.
 */
export const reportError = (error: Error, options?: ErrorReportOptions) => {
    // Log to console in development/production
    logger.error('Reported Error:', error);

    if (options?.componentStack) {
        logger.error('Component Stack:', options.componentStack);
    }

    if (options?.extra) {
        logger.error('Extra Info:', options.extra);
    }

    // Integrate with Sentry
    console.error(error, {
        extra: {
            ...options?.extra,
            componentStack: options?.componentStack,
        },
        tags: options?.tags,
    });

    analytics.track('client_error', {
        component: options?.tags?.component,
        severity: 'error',
    });
};

/**
 * Creates a wrapped function that automatically reports errors
 */
export const withErrorReporting = <T extends (...args: any[]) => any>(
    fn: T,
    tags?: Record<string, string>
): T => {
    return ((...args: Parameters<T>): ReturnType<T> => {
        try {
            const result = fn(...args);
            if (result instanceof Promise) {
                return result.catch((error: Error) => {
                    reportError(error, { tags });
                    throw error;
                }) as ReturnType<T>;
            }
            return result;
        } catch (error) {
            reportError(error as Error, { tags });
            throw error;
        }
    }) as T;
};
